import { useContext, useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Bowser from "bowser";

import { DarkModeContext } from "../../context/darkModeContext";
import ActivityLog from "../../pages/activity/ActivityLog";

import {
  BsBell,
  BsBoxArrowLeft,
  BsBoxes,
  BsCartPlus,
  BsClipboard2Check,
  BsFilePerson,
  BsGear,
  BsHouseDoorFill,
  BsList,
  BsListUl,
  BsPerson,
  BsReceipt,
} from "react-icons/bs";

import "./sidebar.css";
import { clearAuthSession, isManager } from "../../auth/auth";

const MENU_SECTIONS = [
  {
    title: "MAIN",
    items: [
      {
        label: "Dashboard",
        path: "/",
        icon: BsHouseDoorFill,
        end: true,
      },
    ],
  },
  {
    title: "LISTS",
    items: [
      {
        label: "Employees",
        path: "/employees",
        icon: BsPerson,
      },
      {
        label: "Inventory",
        path: "/inventory",
        icon: BsBoxes,
      },
      {
        label: "Manage Jobs",
        path: "/managejobs",
        icon: BsClipboard2Check,
      },
      {
        label: "Inventory Alert",
        path: "/inventory-alert",
        icon: BsBell,
      },
      {
        label: "Orders",
        path: "/orders",
        icon: BsCartPlus,
      },
    ],
  },
  {
    title: "SERVICE",
    items: [
      {
        label: "Logs",
        path: "/logs",
        icon: BsReceipt,
      },
      {
        label: "Settings",
        path: "/settings",
        icon: BsGear,
      },
    ],
  },
  {
    title: "USER",
    items: [
      {
        label: "Profile",
        path: "/my-profile",
        icon: BsFilePerson,
      },
    ],
  },
];

const Sidebar = () => {
  const navigate = useNavigate();
  const { dispatch } = useContext(DarkModeContext);

  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth <= 768;
  });

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768) {
        setCollapsed(true);
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleLogout = () => {
    try {
      const { name, version } = Bowser.getParser(
        window.navigator.userAgent,
      ).getBrowser();

      clearAuthSession();

      ActivityLog(
        "Logout Success",
        `Logged out from ${name}, version ${version}`,
      );
      toast.info("Logged out successfully!");
      navigate("/login", { replace: true });
    } catch (error) {
      console.error("Logout error:", error);
      toast.error("Failed to log out. Please try again.");
    }
  };

  if (!isManager()) return null;

  return (
    <aside className={`sidebar ${collapsed ? "collapsed" : ""}`}>
      <div className="sidebar-main">
        <div className="sidebar-header">
          <div className="sidebar-brand">
            <div className="sidebar-brand-mark">TT</div>

            <div className="sidebar-brand-text">
              <strong>Trucktronix</strong>
              <span>IMS</span>
            </div>
          </div>

          <button
            type="button"
            className="sidebar-toggle"
            onClick={() => setCollapsed((previous) => !previous)}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? <BsList /> : <BsListUl />}
          </button>
        </div>

        <nav className="sidebar-nav">
          {MENU_SECTIONS.map((section) => (
            <div className="sidebar-section" key={section.title}>
              <p className="sidebar-section-title">{section.title}</p>

              <ul className="sidebar-menu">
                {section.items.map((item) => {
                  const Icon = item.icon;

                  return (
                    <li key={item.path}>
                      <NavLink
                        to={item.path}
                        end={item.end}
                        title={collapsed ? item.label : undefined}
                        className={({ isActive }) =>
                          `sidebar-link ${isActive ? "active" : ""}`
                        }
                      >
                        <Icon className="sidebar-icon" />
                        <span className="sidebar-label">{item.label}</span>
                      </NavLink>
                    </li>
                  );
                })}

                {section.title === "USER" && (
                  <li>
                    <button
                      type="button"
                      className="sidebar-link sidebar-logout"
                      onClick={handleLogout}
                      title={collapsed ? "Logout" : undefined}
                    >
                      <BsBoxArrowLeft className="sidebar-icon" />
                      <span className="sidebar-label">Logout</span>
                    </button>
                  </li>
                )}
              </ul>
            </div>
          ))}
        </nav>
      </div>

      <div className="sidebar-footer">
        <div className="sidebar-theme">
          <span className="sidebar-theme-label">Theme</span>

          <div className="sidebar-theme-options">
            <button
              type="button"
              className="theme-option light"
              onClick={() => dispatch({ type: "LIGHT" })}
              aria-label="Use light theme"
              title="Light theme"
            />

            <button
              type="button"
              className="theme-option dark"
              onClick={() => dispatch({ type: "DARK" })}
              aria-label="Use dark theme"
              title="Dark theme"
            />
          </div>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
